import React, { useState, useContext, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { BsArrowLeft, BsSave, BsX } from 'react-icons/bs';
import { toast } from 'react-toastify';
import Breadcrumbs from '../../components/layout/breadcrumbs';
import Page from '../../components/page';
import SubmitButton from '../../components/form/submit-button';
import { api } from '../../providers/apiClient';
import { ContextUser } from '../../providers/ContextUser';
import { IncidentForm, FormSection, DetailActionButton } from './styles';
import { buildOrgLevelBreadcrumbs } from '../../utils/breadcrumbUtils';

const CreateIncident = ({ isCollapsed }) => {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const { currentOrganization, currentUser } = useContext(ContextUser);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    incidentDate: '',
    discoveryDate: '',
    incidentType: '',
    severityLevel: '',
    dataCategories: [],
    dataSubjectsCount: 0,
    affectedSystems: [],
    rootCause: '',
    technicalDetails: '',
    immediateMeasures: '',
    containmentMeasures: '',
    recoveryMeasures: '',
    preventiveMeasures: ''
  });
  
  // Estados separados para inputs de array (permitem digitação livre)
  const [dataCategoriesInput, setDataCategoriesInput] = useState('');
  const [affectedSystemsInput, setAffectedSystemsInput] = useState('');

  const incidentTypes = [
    { value: 'VAZAMENTO_DADOS', label: 'Vazamento de Dados' },
    { value: 'ACESSO_NAO_AUTORIZADO', label: 'Acesso Não Autorizado' },
    { value: 'PERDA_DADOS', label: 'Perda de Dados' },
    { value: 'ALTERACAO_NAO_AUTORIZADA', label: 'Alteração Não Autorizada' },
    { value: 'DESTRUICAO_DADOS', label: 'Destruição de Dados' },
    { value: 'OUTROS', label: 'Outros' }
  ];

  const severityLevels = [
    { value: 'BAIXA', label: 'Baixa' },
    { value: 'MEDIA', label: 'Média' },
    { value: 'ALTA', label: 'Alta' },
    { value: 'CRITICA', label: 'Crítica' }
  ];

  const dataCategories = [
    'DADOS_PESSOAIS',
    'DADOS_SENSIVEIS',
    'DADOS_BIOMETRICOS',
    'DADOS_SAUDE',
    'DADOS_FINANCEIROS',
    'DADOS_LOCALIZACAO',
    'DADOS_NAVEGACAO',
    'DADOS_COMUNICACAO'
  ];

  // Configurar breadcrumbs quando a organização estiver disponível
  useEffect(() => {
    if (currentOrganization?.id) {
      const breadcrumbs = buildOrgLevelBreadcrumbs(
        currentOrganization,
        'Novo Incidente',
        `/prefeitura/${orgId}/incidents/create`
      );
      setBreadcrumbs(breadcrumbs);
    }
  }, [currentOrganization, orgId]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpar erro do campo
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleArrayChange = (field, value) => {
    const array = value.split(',').map(item => item.trim()).filter(item => item);
    handleInputChange(field, array);
  };

  const scrollToFirstError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    
    if (firstErrorField) {
      // Mapear os nomes dos campos para os IDs reais
      const fieldIdMap = {
        'title': 'incident-title',
        'description': 'incident-description',
        'incidentDate': 'incident-date',
        'discoveryDate': 'discovery-date',
        'incidentType': 'incident-type',
        'severityLevel': 'severity-level',
        'dataCategories': 'data-categories',
        'dataSubjectsCount': 'data-subjects-count',
        'affectedSystems': 'affected-systems',
        'rootCause': 'root-cause',
        'technicalDetails': 'technical-details',
        'immediateMeasures': 'immediate-measures',
        'containmentMeasures': 'containment-measures',
        'recoveryMeasures': 'recovery-measures',
        'preventiveMeasures': 'preventive-measures'
      };
      
      const elementId = fieldIdMap[firstErrorField] || firstErrorField;
      const element = document.getElementById(elementId);
      
      if (element) {
        // Destacar o elemento visualmente
        element.style.border = '2px solid #dc3545';
        element.style.boxShadow = '0 0 0 0.2rem rgba(220, 53, 69, 0.25)';
        
        // Remover destaque após 3 segundos
        setTimeout(() => {
          element.style.border = '';
          element.style.boxShadow = '';
        }, 3000);
        
        // Tentar fazer scroll para o elemento
        try {
          element.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
        } catch (error) {
          // Fallback: usar scrollTo se scrollIntoView falhar
          const elementRect = element.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;
          const middle = absoluteElementTop - (window.innerHeight / 2);
          
          window.scrollTo({
            top: middle,
            behavior: 'smooth'
          });
        }
        
        // Tentar focar no elemento após o scroll
        setTimeout(() => {
          try {
            // Se for um select, tentar abrir o dropdown
            if (element.tagName === 'SELECT') {
              element.focus();
              element.click();
            } else if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
              element.focus();
            } else {
              // Para outros elementos, tentar focar no primeiro input/textarea dentro do grupo
              const inputElement = element.querySelector('input, textarea, select');
              if (inputElement) {
                inputElement.focus();
                if (inputElement.tagName === 'SELECT') {
                  inputElement.click();
                }
              }
            }
          } catch (error) {
            // Silenciar erro de foco
          }
        }, 800);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Título é obrigatório';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    }

    if (!formData.incidentDate) {
      newErrors.incidentDate = 'Data do incidente é obrigatória';
    }

    if (!formData.discoveryDate) {
      newErrors.discoveryDate = 'Data de descoberta é obrigatória';
    }

    if (!formData.incidentType) {
      newErrors.incidentType = 'Tipo do incidente é obrigatório';
    }

    if (!formData.severityLevel) {
      newErrors.severityLevel = 'Nível de severidade é obrigatório';
    }

    // Validar datas
    if (formData.incidentDate && formData.discoveryDate) {
      if (new Date(formData.incidentDate) > new Date(formData.discoveryDate)) {
        newErrors.discoveryDate = 'Data de descoberta deve ser posterior à data do incidente';
      }
    }

    setErrors(newErrors);
    
    // Se há erros, mostrar toast e fazer scroll
    if (Object.keys(newErrors).length > 0) {
      toast.error('Por favor, preencha todos os campos obrigatórios corretamente.', {
        style: {
          width: '600px',
          maxWidth: '90vw',
          fontSize: '14px',
          padding: '12px 20px',
          borderRadius: '8px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }
      });
      
      // Fazer scroll para o primeiro campo com erro
      scrollToFirstError(newErrors);
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!currentOrganization?.id) {
      toast.error('Organização não encontrada. Faça login novamente.', {
        style: {
          width: '500px',
          maxWidth: '90vw',
          fontSize: '14px',
          padding: '12px 20px',
          borderRadius: '8px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }
      });
      return;
    }

    if (!currentUser?.id) {
      toast.error('Usuário não encontrado. Faça login novamente.', {
        style: {
          width: '500px',
          maxWidth: '90vw',
          fontSize: '14px',
          padding: '12px 20px',
          borderRadius: '8px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const incidentData = {
        ...formData,
        orgId: currentOrganization.id,
        dpoId: currentUser.id,
        dataSubjectsCount: parseInt(formData.dataSubjectsCount) || 0
      };

      const response = await api.post('/incidents', incidentData);
      
      // Mostrar toast de sucesso
      toast.success('Incidente criado com sucesso!');
      
      // Redirecionar para a página de detalhes do incidente criado
      navigate(`/prefeitura/${orgId}/incidents/${response.data.id}`);
      
    } catch (error) {
      console.error('Erro ao criar incidente:', error);
      toast.error('Erro ao criar incidente. Tente novamente.', {
        style: {
          width: '500px',
          maxWidth: '90vw',
          fontSize: '14px',
          padding: '12px 20px',
          borderRadius: '8px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/prefeitura/${orgId}/incidents`);
  };

  return (
    <>
      <Breadcrumbs id='breadcrumbs-create-incident' path={breadcrumbs} />
      <Page id='page-create-incident' title='Novo Incidente LGPD'>
        <Container fluid>
          <Row>
            <Col>
              <Card>
                <Card.Header className='d-flex justify-content-between align-items-center'>
                  <h5 className='mb-0'>Criar Novo Incidente</h5>
                  <div className='d-flex gap-2'>
                    <DetailActionButton
                      className='action-button secondary'
                      onClick={handleCancel}
                      id='cancel-incident-btn'
                      title='Cancelar criação'
                    >
                      <BsX />
                      Cancelar
                    </DetailActionButton>
                    <DetailActionButton
                      className='action-button secondary'
                      onClick={() => navigate(`/prefeitura/${orgId}/incidents`)}
                      id='back-incidents-btn'
                      title='Voltar para lista de incidentes'
                    >
                      <BsArrowLeft />
                      Voltar
                    </DetailActionButton>
                  </div>
                </Card.Header>
                
                <Card.Body>
                  <Form onSubmit={handleSubmit}>
                    {/* Informações Básicas */}
                    <FormSection className='form-section'>
                      <div className='section-title'>Informações Básicas</div>
                      
                      <Row>
                        <Col md={12}>
                          <Form.Group className='mb-3'>
                            <Form.Label>
                              Título do Incidente <span className='required-field'>*</span>
                            </Form.Label>
                            <Form.Control
                              type='text'
                              value={formData.title}
                              onChange={(e) => handleInputChange('title', e.target.value)}
                              isInvalid={!!errors.title}
                              id='incident-title'
                              placeholder='Ex: Vazamento de dados pessoais em sistema de RH'
                            />
                            <Form.Control.Feedback type='invalid'>
                              {errors.title}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12}>
                          <Form.Group className='mb-3'>
                            <Form.Label>
                              Descrição Detalhada <span className='required-field'>*</span>
                            </Form.Label>
                            <Form.Control
                              as='textarea'
                              rows={4}
                              value={formData.description}
                              onChange={(e) => handleInputChange('description', e.target.value)}
                              isInvalid={!!errors.description}
                              id='incident-description'
                              placeholder='Descreva detalhadamente o que aconteceu, quando, onde e como foi descoberto...'
                            />
                            <Form.Control.Feedback type='invalid'>
                              {errors.description}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className='mb-3'>
                            <Form.Label>
                              Data do Incidente <span className='required-field'>*</span>
                            </Form.Label>
                            <Form.Control
                              type='datetime-local'
                              value={formData.incidentDate}
                              onChange={(e) => handleInputChange('incidentDate', e.target.value)}
                              isInvalid={!!errors.incidentDate}
                              id='incident-date'
                            />
                            <Form.Control.Feedback type='invalid'>
                              {errors.incidentDate}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        
                        <Col md={6}>
                          <Form.Group className='mb-3'>
                            <Form.Label>
                              Data de Descoberta <span className='required-field'>*</span>
                            </Form.Label>
                            <Form.Control
                              type='datetime-local'
                              value={formData.discoveryDate}
                              onChange={(e) => handleInputChange('discoveryDate', e.target.value)}
                              isInvalid={!!errors.discoveryDate}
                              id='discovery-date'
                            />
                            <Form.Control.Feedback type='invalid'>
                              {errors.discoveryDate}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </FormSection>

                    {/* Classificação do Incidente */}
                    <FormSection className='form-section'>
                      <div className='section-title'>Classificação do Incidente</div>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className='mb-3'>
                            <Form.Label>
                              Tipo de Incidente <span className='required-field'>*</span>
                            </Form.Label>
                            <Form.Select
                              value={formData.incidentType}
                              onChange={(e) => handleInputChange('incidentType', e.target.value)}
                              isInvalid={!!errors.incidentType}
                              id='incident-type'
                            >
                              <option value=''>Selecione o tipo</option>
                              {incidentTypes.map(type => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type='invalid'>
                              {errors.incidentType}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        
                        <Col md={6}>
                          <Form.Group className='mb-3'>
                            <Form.Label>
                              Nível de Severidade <span className='required-field'>*</span>
                            </Form.Label>
                            <Form.Select
                              value={formData.severityLevel}
                              onChange={(e) => handleInputChange('severityLevel', e.target.value)}
                              isInvalid={!!errors.severityLevel}
                              id='severity-level'
                            >
                              <option value=''>Selecione a severidade</option>
                              {severityLevels.map(level => (
                                <option key={level.value} value={level.value}>
                                  {level.label}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type='invalid'>
                              {errors.severityLevel}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className='mb-3'>
                            <Form.Label>Categorias de Dados Afetados</Form.Label>
                            <Form.Control
                              type='text'
                              value={dataCategoriesInput}
                              onChange={(e) => setDataCategoriesInput(e.target.value)}
                              onBlur={() => {
                                const array = dataCategoriesInput.split(',').map(item => item.trim()).filter(item => item);
                                setFormData(prev => ({ ...prev, dataCategories: array }));
                              }}
                              id='data-categories'
                              placeholder='Ex: DADOS_PESSOAIS, DADOS_SENSIVEIS'
                              autoComplete='off'
                              spellCheck='false'
                            />
                            <Form.Text className='text-muted'>
                              Separe as categorias por vírgula
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        
                        <Col md={6}>
                          <Form.Group className='mb-3'>
                            <Form.Label>Número de Titulares Afetados</Form.Label>
                            <Form.Control
                              type='number'
                              min='0'
                              value={formData.dataSubjectsCount}
                              onChange={(e) => handleInputChange('dataSubjectsCount', e.target.value)}
                              id='data-subjects-count'
                              placeholder='0'
                            />
                            <Form.Text className='text-muted'>
                              Estimativa do número de pessoas afetadas
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>
                    </FormSection>

                    {/* Detalhes Técnicos */}
                    <FormSection className='form-section'>
                      <div className='section-title'>Detalhes Técnicos</div>
                      
                      <Row>
                        <Col md={12}>
                          <Form.Group className='mb-3'>
                            <Form.Label>Sistemas Afetados</Form.Label>
                            <Form.Control
                              type='text'
                              value={affectedSystemsInput}
                              onChange={(e) => setAffectedSystemsInput(e.target.value)}
                              onBlur={() => {
                                const array = affectedSystemsInput.split(',').map(item => item.trim()).filter(item => item);
                                setFormData(prev => ({ ...prev, affectedSystems: array }));
                              }}
                              id='affected-systems'
                              placeholder='Ex: Sistema RH, Base de dados funcionários'
                            />
                            <Form.Text className='text-muted'>
                              Separe os sistemas por vírgula
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12}>
                          <Form.Group className='mb-3'>
                            <Form.Label>Causa Raiz</Form.Label>
                            <Form.Control
                              as='textarea'
                              rows={3}
                              value={formData.rootCause}
                              onChange={(e) => handleInputChange('rootCause', e.target.value)}
                              id='root-cause'
                              placeholder='Descreva a causa raiz do incidente...'
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12}>
                          <Form.Group className='mb-3'>
                            <Form.Label>Detalhes Técnicos</Form.Label>
                            <Form.Control
                              as='textarea'
                              rows={4}
                              value={formData.technicalDetails}
                              onChange={(e) => handleInputChange('technicalDetails', e.target.value)}
                              id='technical-details'
                              placeholder='Descreva os detalhes técnicos do incidente...'
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </FormSection>

                    {/* Medidas Tomadas */}
                    <FormSection className='form-section'>
                      <div className='section-title'>Medidas Tomadas</div>
                      
                      <Row>
                        <Col md={12}>
                          <Form.Group className='mb-3'>
                            <Form.Label>Medidas Imediatas</Form.Label>
                            <Form.Control
                              as='textarea'
                              rows={3}
                              value={formData.immediateMeasures}
                              onChange={(e) => handleInputChange('immediateMeasures', e.target.value)}
                              id='immediate-measures'
                              placeholder='Descreva as medidas imediatas tomadas...'
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className='mb-3'>
                            <Form.Label>Medidas de Contenção</Form.Label>
                            <Form.Control
                              as='textarea'
                              rows={3}
                              value={formData.containmentMeasures}
                              onChange={(e) => handleInputChange('containmentMeasures', e.target.value)}
                              id='containment-measures'
                              placeholder='Descreva as medidas de contenção...'
                            />
                          </Form.Group>
                        </Col>
                        
                        <Col md={6}>
                          <Form.Group className='mb-3'>
                            <Form.Label>Medidas de Recuperação</Form.Label>
                            <Form.Control
                              as='textarea'
                              rows={3}
                              value={formData.recoveryMeasures}
                              onChange={(e) => handleInputChange('recoveryMeasures', e.target.value)}
                              id='recovery-measures'
                              placeholder='Descreva as medidas de recuperação...'
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={12}>
                          <Form.Group className='mb-3'>
                            <Form.Label>Medidas Preventivas</Form.Label>
                            <Form.Control
                              as='textarea'
                              rows={3}
                              value={formData.preventiveMeasures}
                              onChange={(e) => handleInputChange('preventiveMeasures', e.target.value)}
                              id='preventive-measures'
                              placeholder='Descreva as medidas preventivas...'
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </FormSection>

                    {/* Botões de Ação */}
                    <div className='d-flex justify-content-left mt-4'>
                      <DetailActionButton
                        type='button'
                        className='action-button secondary'
                        onClick={handleCancel}
                        id='cancel-form-btn'
                        title='Cancelar criação'
                      >
                        Cancelar
                      </DetailActionButton>
                      <SubmitButton 
                        type='submit' 
                        loading={isSubmitting} 
                        block={false}
                        id='submit-incident-btn'
                      >
                        Criar Incidente
                      </SubmitButton>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </Page>
    </>
  );
};

export default CreateIncident;
